import React, { useContext } from 'react';
import HeroSection from '../components/HeroSection';
import PricingCard from '../components/PricingCard';
import TestimonialCard from '../components/TestimonialCard';
import { CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ModalContext } from '../App';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../components/ServicePageLayout.css';

const AmazonBrandRegistry = () => {
  const { openModal } = useContext(ModalContext);
  const [openFaq, setOpenFaq] = React.useState(null);

  const heroData = {
    eyebrow: 'AMAZON BRAND REGISTRY',
    title: 'Protect Your Amazon Brand',
    description: 'Register your brand on Amazon to unlock powerful tools, protect against counterfeiters, and take control of your product listings.',
    backgroundImage: 'https://images.unsplash.com/photo-1638262052640-82e94d64664a',
    showForm: false,
    ctaText: 'Register on Amazon',
    ctaLink: '#pricing'
  };

  const processSteps = [
    {
      title: 'Eligibility Verification',
      description: 'We begin by verifying your trademark registration status and ensuring you meet Amazon Brand Registry requirements. Our specialists review your USPTO registration certificate, confirm your trademark is active and registered (not pending), and verify that you have the necessary rights to enroll your brand in the program.'
    },
    {
      title: 'Application Preparation',
      description: 'Our team prepares your complete Brand Registry application including trademark information, brand logo verification, product category documentation, and manufacturing details. We ensure all required information is accurate and formatted correctly to meet Amazon\'s specific enrollment standards and technical requirements.'
    },
    {
      title: 'Enrollment and Setup',
      description: 'We submit your Brand Registry application to Amazon and monitor the approval process. Once enrolled, we guide you through accessing your brand dashboard, setting up brand protection tools, and understanding how to leverage enhanced content features, reporting mechanisms, and analytics available through the Brand Registry program.'
    }
  ];

  const pricingPlans = [
    { 
      name: 'Standard', 
      price: '99', 
      features: [
        'Amazon Brand Registry Case Review',
        'Trademark Eligibility Assessment',
        'Brand Registry Application Preparation',
        'Amazon Enrollment Application Filing',
        'Brand Logo and Image Verification',
        'Product Category Documentation',
        'Application Submission Confirmation',
        'Enrollment Status Tracking',
        'Basic Email Support',
        '100% Satisfaction Guarantee'
      ] 
    },
    { 
      name: 'Diamond', 
      price: '149', 
      features: [
        'Everything in Standard Package',
        'Comprehensive Application Preparation',
        'Detailed Eligibility Review Process',
        'Enhanced Documentation Package',
        'Trademark Verification Assistance',
        'Brand Content Optimization Guide',
        'Refusal Risk Assessment Meter',
        'Priority Application Processing',
        'Dedicated Support Specialist',
        'Phone and Email Support Access',
        'Application Monitoring Service',
        '100% Satisfaction Guarantee'
      ], 
      isPopular: true 
    },
    { 
      name: 'Premium', 
      price: '199', 
      features: [
        'Everything in Diamond Package',
        'Complete Brand Registry Documentation',
        'Amazon Brand Application Monitoring',
        'Digital Asset File Management',
        'Brand Protection Strategy Guide',
        'A+ Content Setup Guidance',
        'Sponsored Brands Eligibility Review',
        'Dedicated Brand Registry Manager',
        'Priority Customer Support Line',
        'Post-Enrollment Brand Tools Training',
        'Brand Analytics Access Guide',
        'Infringement Reporting Assistance',
        'Brand Store Setup Recommendations',
        'Ongoing Enrollment Support'
      ] 
    }
  ];

  const whyChooseData = {
    paragraphs: [
      'Amazon Brand Registry provides essential tools and protections for brand owners selling on the world largest e-commerce platform. At Unity Trademark, we specialize in helping businesses successfully enroll in Brand Registry to gain access to enhanced content features, proactive brand protection, and powerful search and reporting tools. Our experienced team understands Amazon complex enrollment requirements and ensures your application is prepared correctly for smooth, fast approval.',
      'We know that navigating Amazon Brand Registry enrollment can be confusing, with specific trademark requirements and technical application details. That is why we have streamlined the process to make brand protection accessible for sellers of all sizes. From verifying your trademark eligibility to completing your enrollment and setting up your brand dashboard, we provide expert guidance at every step to ensure you maximize the benefits of Brand Registry membership.'
    ],
    features: [
      {
        title: 'Expert Enrollment Assistance',
        description: 'Our Amazon Brand Registry specialists have successfully enrolled hundreds of brands. We ensure your application meets all requirements and is prepared correctly to avoid delays or rejections during the enrollment process.'
      },
      {
        title: 'Trademark Eligibility Guidance',
        description: 'We verify your trademark registration status and ensure it meets Amazon specific requirements for Brand Registry. If you need trademark registration first, we can coordinate both services for seamless brand protection.'
      },
      {
        title: 'Comprehensive Brand Protection',
        description: 'Beyond enrollment, we guide you through using Brand Registry tools to monitor your brand, report violations, and protect against counterfeiters. Our support helps you leverage the full power of Amazon brand protection features.'
      },
      {
        title: 'Post-Enrollment Support',
        description: 'After successful enrollment, we provide training on accessing enhanced content features, using brand analytics, and managing your Brand Registry account. Our ongoing support ensures you maximize your investment in brand protection.'
      }
    ]
  };

  const testimonials = [
    { name: 'Jennifer Lee', company: 'Amazon Seller', text: 'The Brand Registry enrollment was seamless with their help. They verified my trademark, prepared everything correctly, and my brand was approved quickly. Great service!', image: 'https://i.pravatar.cc/150?img=5' },
    { name: 'Michael Torres', company: 'E-commerce Brand Owner', text: 'I was struggling with the Brand Registry application requirements. Their team made it simple and handled everything professionally. Now I have full control over my listings!', image: 'https://i.pravatar.cc/150?img=12' },
    { name: 'Sarah Kim', company: 'Product Manufacturer', text: 'Excellent guidance on using Brand Registry tools after enrollment. They showed me how to protect my brand and report counterfeiters effectively. Highly recommend!', image: 'https://i.pravatar.cc/150?img=47' },
    { name: 'David Chen', company: 'Private Label Seller', text: 'Professional service from start to finish. They coordinated my trademark registration and Brand Registry enrollment seamlessly. Worth every penny for the brand protection.', image: 'https://i.pravatar.cc/150?img=13' },
    { name: 'Amanda Rodriguez', company: 'Beauty Brand Owner', text: 'The post-enrollment training was invaluable. They helped me set up A+ content and understand all the analytics tools. My brand presence on Amazon has never been stronger!', image: 'https://i.pravatar.cc/150?img=9' }
  ];

  const faqs = [
    {
      question: 'What are the requirements for Amazon Brand Registry?',
      answer: 'To enroll in Amazon Brand Registry, you must have an active registered trademark from a qualifying trademark office (including USPTO). The trademark must be a text-based or image-based mark registered with the USPTO and appear on your products or packaging. Pending trademark applications do not qualify - you must have a fully registered trademark with a registration number.'
    },
    {
      question: 'How long does Brand Registry enrollment take?',
      answer: 'Once your application is submitted, Amazon typically reviews and approves Brand Registry applications within 5-10 business days if all requirements are met. The timeline can vary based on application completeness and Amazon review workload. We monitor your application status and address any questions from Amazon promptly.'
    },
    {
      question: 'What benefits do I get from Amazon Brand Registry?',
      answer: 'Brand Registry provides enhanced brand protection tools, proactive brand monitoring, powerful search capabilities to find policy violations, increased influence over product listings, access to A+ Content for enhanced product descriptions, Brand Stores, Sponsored Brands advertising, and comprehensive reporting tools for intellectual property violations.'
    },
    {
      question: 'Can I enroll if I do not have a trademark yet?',
      answer: 'No, you must have a registered trademark (not pending) to qualify for Brand Registry. However, we can help you obtain trademark registration first, then coordinate your Brand Registry enrollment once your trademark is approved and registered. We offer combined services for efficient brand protection.'
    },
    {
      question: 'What if someone is already selling under my brand name?',
      answer: 'If unauthorized sellers are using your brand name, enrolling in Brand Registry gives you tools to report violations and request removal of infringing listings. Once enrolled, you gain access to Amazon Report a Violation tool and other brand protection features to combat counterfeiters and unauthorized sellers effectively.'
    }
  ];

  return (
    <div className="service-page">
      <HeroSection {...heroData} />

      <section className="section">
        <div className="container">
          <h2 className="section-title">About Amazon Brand Registry</h2>
          <p className="section-subtitle">
            Amazon Brand Registry is a powerful program that helps brand owners protect their intellectual property and create accurate, trusted experiences for customers on Amazon. By enrolling your registered trademark in Brand Registry, you gain access to enhanced tools for finding and reporting policy violations, increased control over product detail pages, proactive brand protection features, and exclusive access to advanced selling programs like A+ Content and Sponsored Brands. Brand Registry empowers you to build and protect your brand on the world largest e-commerce platform.
          </p>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">How We Work</h2>
          <p className="section-subtitle">
            Our streamlined three-step process makes Amazon Brand Registry enrollment simple and efficient. From eligibility verification to post-enrollment support, we guide you through every aspect of brand protection.
          </p>
          <div className="process-grid">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{String(index + 1).padStart(2, '0')}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="section">
        <div className="container">
          <h2 className="section-title">Choose Your Package</h2>
          <p className="section-subtitle">
            Select the Brand Registry enrollment package that fits your needs and budget. All packages include professional service, expert guidance, and comprehensive support throughout the enrollment process.
          </p>
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <PricingCard 
                key={index} 
                {...plan} 
                onGetStarted={(packageName) => openModal(`Amazon Brand Registry - ${packageName}`)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Why Choose Unity Trademark</h2>
          <div className="why-choose-content">
            {whyChooseData.paragraphs.map((paragraph, index) => (
              <p key={index} className="why-choose-paragraph">{paragraph}</p>
            ))}
          </div>
          <div className="features-grid">
            {whyChooseData.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <CheckCircle size={40} className="feature-icon-svg" />
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Protect Your Amazon Brand?</h2>
            <p className="cta-description">
              Get started with Amazon Brand Registry enrollment starting at just $99. Join thousands of successful sellers who trust Unity Trademark for comprehensive brand protection on Amazon.
            </p>
            <Button 
              size="lg" 
              className="cta-button"
              onClick={() => openModal('Amazon Brand Registry')}
            >
              Start Your Brand Registry Enrollment
            </Button>
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Client Success Stories</h2>
          <div className="testimonials-swiper-container">
            <Swiper
              modules={[Autoplay, Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              loop={true}
              breakpoints={{
                640: { slidesPerView: 1, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 25 },
                1024: { slidesPerView: 3, spaceBetween: 30 },
              }}
              className="testimonials-swiper"
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <TestimonialCard {...testimonial} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmazonBrandRegistry;
